import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppEmpty from '@nxsys/components/AppEmpty';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useGetPayBreakdown } from '@nxsys/hooks/api/dashboard/useDashboardApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getBreakdownColor } from './payBreakdownColors';
import PayBreakdownChart, { PayBreakdownItem } from './PayBreakdownChart';
import PayBreakdownModal from './PayBreakdownModal';

const PayBreakdown = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);

  const {
    value: payBreakdownValue,
    loading,
    error,
    run: fetchPayBreakdown,
  } = useGetPayBreakdown();

  useEffect(() => {
    fetchPayBreakdown(undefined);
  }, []);

  const apiData = payBreakdownValue?.data ?? null;

  if (loading && !apiData) {
    return (
      <AppCard className="p-[20px] h-full">
        <Skeleton active />
      </AppCard>
    );
  }

  if (!apiData && !loading && !error) {
    return (
      <AppCard className="p-[20px] h-full">
        <div className="text-[20px] font-[600] text-neutral-900 mb-[6px]">
          {messages['dashboard.payBreakdown.title']}
        </div>
        <AppEmpty />
      </AppCard>
    );
  }

  const breakdownData: PayBreakdownItem[] =
    apiData?.breakdowns?.map((item, index) => ({
      name: item.name,
      value: item.amount,
      color: getBreakdownColor(item.key, index),
      percentage: item.percentage,
    })) ?? [];

  const total = apiData?.total?.amount ?? 0;
  const totalLabel = apiData?.total?.name ?? '';

  return (
    <AppCard className="p-[20px] h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-[24px]">
        <div className="flex-1">
          <div className="text-[20px] font-[600] text-neutral-900 mb-[6px]">
            {messages['dashboard.payBreakdown.title']}
          </div>
          <div className="text-[12px] font-[400] text-neutral-500">
            {messages['dashboard.payBreakdown.subtitle']}
          </div>
        </div>
        <AppButton
          variant="outlined"
          className="flex-shrink-0 !w-[32px] !h-[32px] !p-0 !min-w-0 !border-primary-300 hover:!bg-primary-50 !rounded-[8px]"
          onClick={() => setIsOpenDetailModal(true)}
          leftIcon={
            <Icons.IconArrowExpand
              color={defaultTheme.theme.palette.primary[500]}
            />
          }
        />
      </div>

      {/* Donut Chart */}
      <PayBreakdownChart
        data={breakdownData}
        total={total}
        totalLabel={totalLabel}
      />

      {/* Breakdown Cards */}
      <div className="flex flex-wrap justify-center gap-[20px]">
        {breakdownData?.map((item, index) => (
          <div
            key={index}
            className="w-[150px] h-[120px] bg-neutral-50 rounded-[8px] p-[12px] border border-neutral-100 shadow-sm"
          >
            <div
              className="w-[8px] h-[8px] rounded-full mb-[6px]"
              style={{ backgroundColor: item.color }}
            />
            <div className="text-[12px] font-[500] text-neutral-900 mb-[4px]">
              {item.name}
            </div>
            <div className="text-[16px] font-[600] text-neutral-900 mb-[4px]">
              {formatMoney(item.value, showCurrencyByCountry(payrollCountry))}
            </div>
            <div className="text-[12px] font-[400] text-neutral-500">
              ({item.percentage}%)
            </div>
          </div>
        ))}
      </div>

      <PayBreakdownModal
        isOpen={isOpenDetailModal}
        toggle={() => setIsOpenDetailModal(false)}
      />
    </AppCard>
  );
};

export default PayBreakdown;
